import React, { useEffect } from 'react';

const PubSubPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Publish-Subscribe (Pub/Sub) Pattern - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Pub/Sub pattern for async notifications and event-driven architecture. Learn message brokers, topics, and scalable messaging systems.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Pub/Sub pattern for async notifications and event-driven architecture. Learn message brokers, topics, and scalable messaging systems.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Publish-Subscribe (Pub/Sub) Pattern</h1>
        <p style={styles.subtitle}>
          Enable async notifications and event-driven architecture with decoupled messaging
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is the Pub/Sub Pattern?</h2>
          <p style={styles.paragraph}>
            The Publish-Subscribe (Pub/Sub) pattern is a messaging paradigm where publishers send messages to topics 
            without knowing who will receive them, and subscribers receive messages from topics they're interested in 
            without knowing who sent them. This creates a loosely coupled, scalable communication system.
          </p>
          <p style={styles.paragraph}>
            In Pub/Sub, publishers and subscribers are decoupled through an intermediary message broker that handles 
            message routing, delivery, and storage. This pattern enables building event-driven architectures where 
            components can react to events asynchronously without direct dependencies.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Core Components</h2>
          
          <h3 style={styles.heading}>Publishers</h3>
          <p style={styles.paragraph}>
            Publishers are components that produce and send messages to topics. They don't need to know about 
            subscribers and can send messages asynchronously.
          </p>
          <pre style={styles.codeBlock}>
{`Publisher Characteristics:

Responsibilities:
- Create and format messages
- Send messages to specific topics
- Handle publishing failures and retries

Example Publisher:
class OrderEventPublisher:
    def __init__(self, message_broker):
        self.broker = message_broker
    
    def publish_order_created(self, order):
        event = {
            'event_type': 'order.created',
            'order_id': order.id,
            'customer_id': order.customer_id,
            'total_amount': order.total,
            'timestamp': datetime.utcnow().isoformat(),
            'items': [item.to_dict() for item in order.items]
        }
        
        self.broker.publish(
            topic='order-events',
            message=event,
            key=str(order.id)  # For partitioning
        )
    
    def publish_order_cancelled(self, order_id, reason):
        event = {
            'event_type': 'order.cancelled',
            'order_id': order_id,
            'reason': reason,
            'timestamp': datetime.utcnow().isoformat()
        }
        
        self.broker.publish(
            topic='order-events',
            message=event,
            key=str(order_id)
        )

Benefits:
✓ Fire-and-forget messaging
✓ No knowledge of subscribers needed
✓ Can publish to multiple topics
✓ Async operation doesn't block business logic`}
          </pre>

          <h3 style={styles.heading}>Subscribers</h3>
          <p style={styles.paragraph}>
            Subscribers are components that receive and process messages from topics they're interested in. 
            They register their interest in specific topics and handle incoming messages.
          </p>
          <pre style={styles.codeBlock}>
{`Subscriber Characteristics:

Responsibilities:
- Subscribe to relevant topics
- Process incoming messages
- Handle message acknowledgment
- Implement error handling and retries

Example Subscriber:
class InventoryService:
    def __init__(self, message_broker):
        self.broker = message_broker
        self.broker.subscribe(
            topic='order-events',
            callback=self.handle_order_event,
            group_id='inventory-service'
        )
    
    def handle_order_event(self, message):
        try:
            event = json.loads(message.value)
            
            if event['event_type'] == 'order.created':
                self.reserve_inventory(event)
            elif event['event_type'] == 'order.cancelled':
                self.release_inventory(event)
            
            # Acknowledge successful processing
            message.ack()
            
        except Exception as e:
            logger.error(f"Failed to process order event: {e}")
            # Don't acknowledge - message will be retried
            message.nack()
    
    def reserve_inventory(self, order_event):
        for item in order_event['items']:
            self.inventory_db.reserve_item(
                item['product_id'], 
                item['quantity']
            )
        
        # Publish inventory reserved event
        self.broker.publish(
            topic='inventory-events',
            message={
                'event_type': 'inventory.reserved',
                'order_id': order_event['order_id'],
                'timestamp': datetime.utcnow().isoformat()
            }
        )

Benefits:
✓ Selective message consumption
✓ Multiple subscribers per topic
✓ Independent processing
✓ Can subscribe to multiple topics`}
          </pre>

          <h3 style={styles.heading}>Message Broker</h3>
          <p style={styles.paragraph}>
            The message broker is the intermediary that receives messages from publishers and delivers them to 
            subscribers. It handles routing, persistence, and delivery guarantees.
          </p>
          <pre style={styles.codeBlock}>
{`Message Broker Responsibilities:

Core Functions:
- Message routing and delivery
- Topic management
- Subscriber registration
- Message persistence
- Delivery guarantees
- Load balancing across subscribers

Popular Message Brokers:
1. Apache Kafka - High-throughput, distributed
2. RabbitMQ - Feature-rich, AMQP protocol
3. Apache Pulsar - Multi-tenant, geo-replication
4. Redis Pub/Sub - Simple, in-memory
5. AWS SNS/SQS - Managed cloud service
6. Google Pub/Sub - Serverless messaging
7. Apache ActiveMQ - JMS-compliant

Broker Architecture:
┌─────────────┐    Topics    ┌─────────────┐
│ Publisher 1 │ ──────────→ │             │
├─────────────┤              │   Message   │
│ Publisher 2 │ ──────────→ │   Broker    │ ──→ Subscriber 1
├─────────────┤              │             │ ──→ Subscriber 2
│ Publisher 3 │ ──────────→ │             │ ──→ Subscriber 3
└─────────────┘              └─────────────┘

Features:
✓ Message persistence
✓ Multiple delivery modes
✓ Topic partitioning
✓ Consumer groups
✓ Dead letter queues
✓ Message ordering
✓ Filtering and routing`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Examples</h2>
          
          <h3 style={styles.heading}>Redis Pub/Sub Implementation</h3>
          <p style={styles.paragraph}>
            Redis provides a simple Pub/Sub implementation suitable for real-time messaging and notifications:
          </p>
          <pre style={styles.codeBlock}>
{`Redis Pub/Sub Example:

import redis
import json
import threading
from typing import Callable

class RedisPubSub:
    def __init__(self, host='localhost', port=6379, db=0):
        self.redis_client = redis.Redis(host=host, port=port, db=db)
        self.pubsub = self.redis_client.pubsub()
        self.subscribers = {}
        self.listener_thread = None
    
    def publish(self, topic: str, message: dict):
        """Publish message to topic"""
        serialized_message = json.dumps(message)
        return self.redis_client.publish(topic, serialized_message)
    
    def subscribe(self, topic: str, callback: Callable):
        """Subscribe to topic with callback function"""
        if topic not in self.subscribers:
            self.subscribers[topic] = []
            self.pubsub.subscribe(topic)
        
        self.subscribers[topic].append(callback)
        
        # Start listener thread if not already running
        if not self.listener_thread or not self.listener_thread.is_alive():
            self.listener_thread = threading.Thread(
                target=self._listen_for_messages,
                daemon=True
            )
            self.listener_thread.start()
    
    def unsubscribe(self, topic: str, callback: Callable = None):
        """Unsubscribe from topic"""
        if topic in self.subscribers:
            if callback:
                self.subscribers[topic].remove(callback)
            else:
                self.subscribers[topic].clear()
            
            if not self.subscribers[topic]:
                self.pubsub.unsubscribe(topic)
                del self.subscribers[topic]
    
    def _listen_for_messages(self):
        """Listen for incoming messages"""
        for message in self.pubsub.listen():
            if message['type'] == 'message':
                topic = message['channel'].decode('utf-8')
                data = json.loads(message['data'].decode('utf-8'))
                
                # Call all subscribers for this topic
                for callback in self.subscribers.get(topic, []):
                    try:
                        callback(topic, data)
                    except Exception as e:
                        print(f"Error in subscriber callback: {e}")

# Usage Example
pubsub = RedisPubSub()

# Publisher
def publish_user_event(user_id, event_type, data):
    event = {
        'user_id': user_id,
        'event_type': event_type,
        'data': data,
        'timestamp': time.time()
    }
    pubsub.publish('user-events', event)

# Subscribers
def email_service_handler(topic, message):
    if message['event_type'] == 'user.registered':
        send_welcome_email(message['user_id'])

def analytics_handler(topic, message):
    track_user_event(
        message['user_id'],
        message['event_type'],
        message['data']
    )

# Subscribe to events
pubsub.subscribe('user-events', email_service_handler)
pubsub.subscribe('user-events', analytics_handler)

# Publish events
publish_user_event(123, 'user.registered', {'email': 'user@example.com'})`}
          </pre>

          <h3 style={styles.heading}>Apache Kafka Implementation</h3>
          <p style={styles.paragraph}>
            Kafka provides a robust, distributed Pub/Sub system with high throughput and fault tolerance:
          </p>
          <pre style={styles.codeBlock}>
{`Kafka Pub/Sub Example:

from kafka import KafkaProducer, KafkaConsumer
import json
import threading
from typing import Dict, Callable

class KafkaPubSub:
    def __init__(self, bootstrap_servers=['localhost:9092']):
        self.bootstrap_servers = bootstrap_servers
        self.producer = KafkaProducer(
            bootstrap_servers=bootstrap_servers,
            value_serializer=lambda x: json.dumps(x).encode('utf-8'),
            key_serializer=lambda x: x.encode('utf-8') if x else None
        )
        self.consumers = {}
        self.consumer_threads = {}
    
    def publish(self, topic: str, message: dict, key: str = None):
        """Publish message to Kafka topic"""
        future = self.producer.send(topic, value=message, key=key)
        
        # Optional: wait for acknowledgment
        try:
            record_metadata = future.get(timeout=10)
            return {
                'topic': record_metadata.topic,
                'partition': record_metadata.partition,
                'offset': record_metadata.offset
            }
        except Exception as e:
            print(f"Failed to publish message: {e}")
            return None
    
    def subscribe(self, topic: str, callback: Callable, group_id: str = None):
        """Subscribe to Kafka topic"""
        consumer = KafkaConsumer(
            topic,
            bootstrap_servers=self.bootstrap_servers,
            group_id=group_id,
            value_deserializer=lambda x: json.loads(x.decode('utf-8')),
            key_deserializer=lambda x: x.decode('utf-8') if x else None,
            auto_offset_reset='latest',
            enable_auto_commit=True
        )
        
        consumer_id = f"{topic}_{group_id or 'default'}"
        self.consumers[consumer_id] = consumer
        
        # Start consumer thread
        thread = threading.Thread(
            target=self._consume_messages,
            args=(consumer, callback),
            daemon=True
        )
        thread.start()
        self.consumer_threads[consumer_id] = thread
    
    def _consume_messages(self, consumer, callback):
        """Consume messages from Kafka"""
        for message in consumer:
            try:
                callback(
                    topic=message.topic,
                    key=message.key,
                    value=message.value,
                    partition=message.partition,
                    offset=message.offset
                )
            except Exception as e:
                print(f"Error processing message: {e}")
    
    def close(self):
        """Close all connections"""
        self.producer.close()
        for consumer in self.consumers.values():
            consumer.close()

# Event-Driven E-commerce Example
kafka_pubsub = KafkaPubSub()

class OrderService:
    def create_order(self, order_data):
        # Create order in database
        order = self.save_order(order_data)
        
        # Publish order created event
        kafka_pubsub.publish(
            topic='order-events',
            message={
                'event_type': 'order.created',
                'order_id': order.id,
                'customer_id': order.customer_id,
                'items': order.items,
                'total': order.total
            },
            key=str(order.id)
        )

class InventoryService:
    def __init__(self):
        kafka_pubsub.subscribe(
            topic='order-events',
            callback=self.handle_order_event,
            group_id='inventory-service'
        )
    
    def handle_order_event(self, topic, key, value, partition, offset):
        if value['event_type'] == 'order.created':
            # Reserve inventory
            self.reserve_items(value['items'])
            
            # Publish inventory event
            kafka_pubsub.publish(
                topic='inventory-events',
                message={
                    'event_type': 'inventory.reserved',
                    'order_id': value['order_id']
                }
            )

class EmailService:
    def __init__(self):
        kafka_pubsub.subscribe(
            topic='order-events',
            callback=self.handle_order_event,
            group_id='email-service'
        )
    
    def handle_order_event(self, topic, key, value, partition, offset):
        if value['event_type'] == 'order.created':
            self.send_order_confirmation(
                value['customer_id'],
                value['order_id']
            )`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Patterns</h2>
          
          <h3 style={styles.heading}>Topic Hierarchies and Wildcards</h3>
          <p style={styles.paragraph}>
            Organize topics hierarchically and use wildcards for flexible subscription patterns:
          </p>
          <pre style={styles.codeBlock}>
{`Topic Hierarchy Examples:

Hierarchical Topics:
- user.events.registered
- user.events.login
- user.events.logout
- order.events.created
- order.events.paid
- order.events.shipped
- order.events.delivered
- inventory.events.low_stock
- inventory.events.restocked

Wildcard Subscriptions:
# Subscribe to all user events
subscribe('user.events.*')

# Subscribe to all events
subscribe('*')

# Subscribe to all order and inventory events
subscribe(['order.events.*', 'inventory.events.*'])

MQTT-style Wildcards:
# Single level wildcard (+)
user/+/events  # Matches user/123/events, user/456/events

# Multi-level wildcard (#)
user/#  # Matches user/123/events/login, user/456/profile/updated

RabbitMQ Routing Patterns:
# Direct exchange - exact match
routing_key = 'order.created'

# Topic exchange - pattern matching
routing_key = 'order.*'  # Matches order.created, order.updated
routing_key = 'order.#'  # Matches order.created.urgent, order.updated.status

Implementation:
class TopicMatcher:
    def __init__(self):
        self.subscriptions = {}
    
    def subscribe(self, pattern, callback):
        if pattern not in self.subscriptions:
            self.subscriptions[pattern] = []
        self.subscriptions[pattern].append(callback)
    
    def publish(self, topic, message):
        matching_callbacks = []
        
        for pattern, callbacks in self.subscriptions.items():
            if self.matches_pattern(topic, pattern):
                matching_callbacks.extend(callbacks)
        
        for callback in matching_callbacks:
            callback(topic, message)
    
    def matches_pattern(self, topic, pattern):
        if '*' not in pattern:
            return topic == pattern
        
        topic_parts = topic.split('.')
        pattern_parts = pattern.split('.')
        
        return self._match_parts(topic_parts, pattern_parts)
    
    def _match_parts(self, topic_parts, pattern_parts):
        if not pattern_parts:
            return not topic_parts
        
        if pattern_parts[0] == '*':
            if len(topic_parts) == 0:
                return False
            return self._match_parts(topic_parts[1:], pattern_parts[1:])
        
        if len(topic_parts) == 0:
            return False
        
        if topic_parts[0] == pattern_parts[0]:
            return self._match_parts(topic_parts[1:], pattern_parts[1:])
        
        return False`}
          </pre>

          <h3 style={styles.heading}>Message Filtering</h3>
          <p style={styles.paragraph}>
            Implement message filtering to allow subscribers to receive only relevant messages:
          </p>
          <pre style={styles.codeBlock}>
{`Message Filtering Implementation:

class FilterablePubSub:
    def __init__(self):
        self.subscriptions = {}
    
    def subscribe_with_filter(self, topic, callback, filter_func=None):
        """Subscribe with optional message filter"""
        if topic not in self.subscriptions:
            self.subscriptions[topic] = []
        
        self.subscriptions[topic].append({
            'callback': callback,
            'filter': filter_func
        })
    
    def publish(self, topic, message):
        """Publish message with filtering"""
        if topic not in self.subscriptions:
            return
        
        for subscription in self.subscriptions[topic]:
            callback = subscription['callback']
            filter_func = subscription['filter']
            
            # Apply filter if provided
            if filter_func is None or filter_func(message):
                callback(topic, message)

# Usage Examples
pubsub = FilterablePubSub()

# Filter by message attributes
def high_priority_filter(message):
    return message.get('priority') == 'high'

def us_region_filter(message):
    return message.get('region') == 'us-east-1'

def large_order_filter(message):
    return message.get('total_amount', 0) > 1000

# Subscribe with filters
pubsub.subscribe_with_filter(
    'order-events',
    handle_high_priority_orders,
    high_priority_filter
)

pubsub.subscribe_with_filter(
    'user-events',
    handle_us_users,
    us_region_filter
)

pubsub.subscribe_with_filter(
    'order-events',
    handle_large_orders,
    large_order_filter
)

# SQL-like Filtering (Advanced)
class SQLFilter:
    def __init__(self, condition):
        self.condition = condition
    
    def __call__(self, message):
        # Simple SQL-like filtering
        # In production, use a proper SQL parser
        if 'priority = high' in self.condition:
            return message.get('priority') == 'high'
        elif 'amount > 1000' in self.condition:
            return message.get('amount', 0) > 1000
        return True

# Subscribe with SQL-like filters
pubsub.subscribe_with_filter(
    'order-events',
    handle_premium_orders,
    SQLFilter("priority = 'high' AND amount > 1000")
)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Delivery Guarantees</h2>
          
          <h3 style={styles.heading}>At-Most-Once Delivery</h3>
          <p style={styles.paragraph}>
            Messages are delivered zero or one time. Fast but may lose messages in case of failures.
          </p>
          <pre style={styles.codeBlock}>
{`At-Most-Once Implementation:

class AtMostOncePubSub:
    def __init__(self):
        self.subscribers = {}
    
    def publish(self, topic, message):
        """Fire-and-forget publishing"""
        if topic in self.subscribers:
            for callback in self.subscribers[topic]:
                try:
                    # No retries - send once only
                    callback(topic, message)
                except Exception as e:
                    # Log error but don't retry
                    logger.error(f"Failed to deliver message: {e}")
    
    def subscribe(self, topic, callback):
        if topic not in self.subscribers:
            self.subscribers[topic] = []
        self.subscribers[topic].append(callback)

Characteristics:
✓ Fastest delivery
✓ No message duplication
✗ Possible message loss
✗ No delivery confirmation

Use Cases:
- Metrics and monitoring
- Real-time notifications
- Non-critical events
- High-frequency data streams`}
          </pre>

          <h3 style={styles.heading}>At-Least-Once Delivery</h3>
          <p style={styles.paragraph}>
            Messages are guaranteed to be delivered but may be delivered multiple times. Requires idempotent processing.
          </p>
          <pre style={styles.codeBlock}>
{`At-Least-Once Implementation:

import time
import uuid
from collections import defaultdict

class AtLeastOncePubSub:
    def __init__(self, retry_attempts=3, retry_delay=1):
        self.subscribers = {}
        self.retry_attempts = retry_attempts
        self.retry_delay = retry_delay
        self.pending_messages = defaultdict(list)
    
    def publish(self, topic, message):
        """Publish with retry mechanism"""
        message_id = str(uuid.uuid4())
        message_with_id = {
            'id': message_id,
            'payload': message,
            'timestamp': time.time()
        }
        
        if topic in self.subscribers:
            for callback in self.subscribers[topic]:
                self._deliver_with_retry(topic, message_with_id, callback)
    
    def _deliver_with_retry(self, topic, message, callback):
        """Deliver message with retry logic"""
        for attempt in range(self.retry_attempts):
            try:
                # Attempt delivery
                ack = callback(topic, message['payload'])
                
                # Check if acknowledged
                if ack is True:
                    return  # Successfully delivered
                
            except Exception as e:
                logger.warning(f"Delivery attempt {attempt + 1} failed: {e}")
            
            # Wait before retry (exponential backoff)
            if attempt < self.retry_attempts - 1:
                time.sleep(self.retry_delay * (2 ** attempt))
        
        # All retries failed - send to dead letter queue
        self._send_to_dlq(topic, message)
    
    def _send_to_dlq(self, topic, message):
        """Send failed message to dead letter queue"""
        dlq_topic = f"{topic}.dlq"
        logger.error(f"Message {message['id']} sent to DLQ: {dlq_topic}")
        # Implementation depends on your DLQ strategy

# Idempotent Message Processing
class IdempotentProcessor:
    def __init__(self):
        self.processed_messages = set()
    
    def process_order_event(self, topic, message):
        message_id = message.get('id')
        
        # Check if already processed
        if message_id in self.processed_messages:
            logger.info(f"Message {message_id} already processed, skipping")
            return True  # Acknowledge as processed
        
        try:
            # Process the message
            self._handle_order_event(message['payload'])
            
            # Mark as processed
            self.processed_messages.add(message_id)
            
            return True  # Acknowledge successful processing
            
        except Exception as e:
            logger.error(f"Failed to process message {message_id}: {e}")
            return False  # Don't acknowledge - will be retried

Characteristics:
✓ No message loss
✓ Delivery confirmation
✗ Possible duplicates
✗ Requires idempotent processing

Use Cases:
- Financial transactions
- Order processing
- Critical business events
- Audit logs`}
          </pre>

          <h3 style={styles.heading}>Exactly-Once Delivery</h3>
          <p style={styles.paragraph}>
            Messages are delivered exactly once. Most complex to implement but provides strongest guarantees.
          </p>
          <pre style={styles.codeBlock}>
{`Exactly-Once Implementation:

class ExactlyOncePubSub:
    def __init__(self, state_store):
        self.subscribers = {}
        self.state_store = state_store  # Persistent state storage
    
    def publish(self, topic, message):
        """Publish with exactly-once semantics"""
        message_id = str(uuid.uuid4())
        
        # Store message state
        self.state_store.store_message_state(message_id, 'pending')
        
        message_with_id = {
            'id': message_id,
            'payload': message,
            'timestamp': time.time()
        }
        
        if topic in self.subscribers:
            for callback in self.subscribers[topic]:
                self._deliver_exactly_once(topic, message_with_id, callback)
    
    def _deliver_exactly_once(self, topic, message, callback):
        """Deliver message with exactly-once guarantee"""
        message_id = message['id']
        
        # Check message state
        state = self.state_store.get_message_state(message_id)
        
        if state == 'delivered':
            return  # Already delivered successfully
        
        if state == 'delivering':
            # Another process is delivering - wait or skip
            return
        
        # Mark as delivering
        self.state_store.store_message_state(message_id, 'delivering')
        
        try:
            # Attempt delivery
            ack = callback(topic, message['payload'])
            
            if ack is True:
                # Mark as delivered
                self.state_store.store_message_state(message_id, 'delivered')
            else:
                # Reset to pending for retry
                self.state_store.store_message_state(message_id, 'pending')
                
        except Exception as e:
            # Reset to pending for retry
            self.state_store.store_message_state(message_id, 'pending')
            raise e

# Transactional Processing
class TransactionalProcessor:
    def __init__(self, db_connection):
        self.db = db_connection
    
    def process_payment_event(self, topic, message):
        message_id = message.get('id')
        
        # Start database transaction
        with self.db.transaction():
            # Check if message already processed
            if self.db.is_message_processed(message_id):
                return True
            
            # Process the payment
            self._process_payment(message['payload'])
            
            # Mark message as processed in same transaction
            self.db.mark_message_processed(message_id)
            
            # Transaction commits automatically
            return True

Characteristics:
✓ No message loss
✓ No duplicates
✓ Strong consistency
✗ Complex implementation
✗ Performance overhead
✗ Requires distributed coordination

Use Cases:
- Banking transactions
- Payment processing
- Critical state changes
- Regulatory compliance scenarios`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Netflix's Event-Driven Architecture</h3>
          <p style={styles.paragraph}>
            Netflix uses Pub/Sub extensively for their microservices architecture, with services publishing events 
            about user activities, content recommendations, and system metrics to enable real-time personalization 
            and monitoring.
          </p>

          <h3 style={styles.heading}>Uber's Real-Time Data Pipeline</h3>
          <p style={styles.paragraph}>
            Uber processes millions of events per second using Kafka-based Pub/Sub for ride matching, pricing, 
            and real-time analytics. Each ride generates events that multiple services consume for different purposes.
          </p>

          <h3 style={styles.heading}>Slack's Message Delivery</h3>
          <p style={styles.paragraph}>
            Slack uses Pub/Sub patterns to deliver messages to multiple clients, handle presence updates, and 
            synchronize state across different devices and platforms in real-time.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Design for idempotency:</strong> Ensure message handlers can safely process the same 
              message multiple times without side effects.
            </li>
            <li style={styles.listItem}>
              <strong>Use meaningful topic names:</strong> Create hierarchical, descriptive topic names that 
              clearly indicate the type and source of events.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper error handling:</strong> Use dead letter queues, retry mechanisms, 
              and circuit breakers to handle failures gracefully.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor message flow:</strong> Track message production, consumption rates, and lag 
              to identify bottlenecks and ensure system health.
            </li>
            <li style={styles.listItem}>
              <strong>Version your message schemas:</strong> Use schema evolution strategies to handle 
              changes in message structure without breaking consumers.
            </li>
            <li style={styles.listItem}>
              <strong>Consider message ordering:</strong> Understand when message ordering is important 
              and design your partitioning strategy accordingly.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            The Pub/Sub pattern is fundamental for building scalable, event-driven architectures. It enables loose 
            coupling between components while providing flexible, asynchronous communication mechanisms.
          </p>
          <p style={styles.paragraph}>
            Choose the right delivery guarantees based on your use case requirements, implement proper error handling 
            and monitoring, and design for idempotency to build robust, scalable messaging systems that can handle 
            real-world production workloads.
          </p>
        </section>
      </main>
    </article>
  );
};

export default PubSubPattern;
